import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { Loader2, LogOut } from 'lucide-react';
import { Toaster } from '@components/ui/sonner';
import { SessionProvider, useSession, ROLE_LABEL } from '@generated/components/session';
import { LoginPage } from '@generated/components/LoginPage';
import { Logo } from '@generated/components/Logo';

export const Route = createFileRoute('/_app')({ component: AppLayout });

function AppLayout() {
  return (
    <SessionProvider>
      <Gate />
      <Toaster />
    </SessionProvider>
  );
}

function Gate() {
  const { ready, user, signOut, isStaff } = useSession();
  if (!ready) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <LoginPage />;
  const nav: { to: '/' | '/review' | '/reporting' | '/settings'; label: string }[] = [{ to: '/', label: 'Library' }];
  if (user.role === 'admin' || user.role === 'reviewer') nav.push({ to: '/review', label: 'To Be Reviewed' });
  if (isStaff || user.role === 'client' || user.permissions.includes('view_reporting')) nav.push({ to: '/reporting', label: 'Reporting' });
  if (user.role === 'admin') nav.push({ to: '/settings', label: 'Settings' });
  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header sticky top-0 z-40">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <Link to="/" aria-label="Library home"><Logo /></Link>
          <nav className="order-3 flex w-full gap-1 overflow-x-auto rounded-full bg-[hsl(var(--track))] p-1 md:order-none md:mx-auto md:w-auto">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} activeOptions={{ exact: n.to === '/' }} className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground transition hover:text-secondary [&.active]:bg-card [&.active]:text-secondary [&.active]:shadow-sm">
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3 md:ml-0">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight text-secondary">{user.fullName}</p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">{ROLE_LABEL[user.role]}</p>
            </div>
            <button onClick={() => void signOut()} aria-label="Sign out" className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-primary/5 hover:text-primary">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8"><Outlet /></main>
    </div>
  );
}
